// Who is at the admin, and what the sign-in screen has to say (UC-20).
//
// The admin page learns how a sign-in ended from the address the server sent
// it back to: ?signin=refused or ?signin=unfinished. The query is read once
// and taken out of the address, so a reload does not show it again. A refused
// account's own Google id comes from the API, which read it into a cookie on
// this browser, never from the address, where it would land in access logs.

import { api } from '../api/client';

export type Notice = 'none' | 'ended' | 'unfinished' | 'refused';

export type View =
	| { kind: 'loading' }
	| { kind: 'signin'; notice: Notice; googleId: string }
	| { kind: 'app'; email: string };

let view = $state<View>({ kind: 'loading' });

export function currentView(): View {
	return view;
}

function signedOut(notice: Notice, googleId = ''): void {
	view = { kind: 'signin', notice, googleId };
}

/** Reads how the last sign-in ended, then who is signed in. */
export async function start(): Promise<void> {
	// Read once and dropped, so a plain match rather than a reactive params object.
	const signin = /[?&]signin=([a-z]+)/.exec(window.location.search)?.[1] ?? null;
	if (signin !== null) {
		window.history.replaceState({}, '', window.location.pathname);
	}

	// A refused sign-in says so even when its Google id cannot be read back:
	// the person must not be sent round the same sign-in with no reason.
	if (signin === 'refused') {
		const { data } = await api.GET('/api/admin/signin/refusal').catch(() => ({ data: undefined }));
		signedOut('refused', data?.googleSubject ?? '');
		return;
	}
	if (signin === 'unfinished') {
		signedOut('unfinished');
		return;
	}

	const { data } = await api.GET('/api/admin/me').catch(() => ({ data: undefined }));
	if (data) {
		view = { kind: 'app', email: data.email };
		return;
	}
	signedOut('none');
}

/** A request answered 401 while signed in: the session ended under the page. */
export function sessionEnded(): void {
	signedOut('ended');
}

export async function signOut(): Promise<void> {
	await api.POST('/api/admin/signout').catch(() => undefined);
	signedOut('none');
}
